// frontend/src/pages/dashboard/AgentDashboard.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import Badge from '../../components/ui/Badge';

export default function AgentDashboard() {
    const { user } = useSelector((state) => state.auth);
    const queryClient = useQueryClient();
    const [isConnected, setIsConnected] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [period, setPeriod] = useState('month');
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    const { data: stats, isLoading: statsLoading, refetch: refetchStats, error: statsError } = useQuery(
        ['agent-stats', period],
        () => api.get(`/dashboard/stats?period=${period}`).then((res) => res.data),
        {
            refetchOnWindowFocus: false,
            staleTime: 30000,
            retry: 2,
            retryDelay: 1000,
        }
    );

    const { data: recentQuotes, isLoading: quotesLoading } = useQuery(
        'agent-recent-quotes',
        () => api.get('/quotes?limit=5').then((res) => res.data),
        {
            refetchOnWindowFocus: false,
            staleTime: 30000,
            retry: 1,
        }
    );

    const { data: recentPolicies, isLoading: policiesLoading } = useQuery(
        'agent-recent-policies',
        () => api.get('/policies?limit=5').then((res) => res.data),
        {
            refetchOnWindowFocus: false,
            staleTime: 30000,
            retry: 1,
        }
    );

    // Connect to SSE for real-time updates
    const connectSSE = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.log('[Dashboard SSE] No token available');
            return;
        }

        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        try {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.0.101:5038/api/v1';
            // Pass token as URL parameter for EventSource
            const url = `${baseUrl}/dashboard/realtime?token=${encodeURIComponent(token)}`;
            console.log('[Dashboard SSE] Connecting to:', url);

            const eventSource = new EventSource(url);

            eventSource.onopen = () => {
                console.log('[Dashboard SSE] Connected');
                setIsConnected(true);
                setReconnectAttempts(0);
                setLastUpdated(new Date());
            };

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log('[Dashboard SSE] Update received:', data);

                    if (data.type === 'stats_update' || data.type === 'connected') {
                        refetchStats();
                        setLastUpdated(new Date());
                    } else if (data.type === 'activity_update') {
                        queryClient.invalidateQueries('agent-stats');
                        queryClient.invalidateQueries('agent-recent-quotes');
                        queryClient.invalidateQueries('agent-recent-policies');
                    }
                } catch (err) {
                    console.error('[Dashboard SSE] Parse error:', err);
                }
            };

            eventSource.onerror = (error) => {
                console.error('[Dashboard SSE] Error:', error);
                setIsConnected(false);

                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                }

                const delay = Math.min(5000 * Math.pow(1.5, reconnectAttempts), 30000);
                console.log(`[Dashboard SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1})`);

                reconnectTimeoutRef.current = setTimeout(() => {
                    setReconnectAttempts(prev => prev + 1);
                    connectSSE();
                }, delay);
            };

            eventSourceRef.current = eventSource;
        } catch (err) {
            console.error('[Dashboard SSE] Connection error:', err);
            setIsConnected(false);
        }
    }, [refetchStats, queryClient, reconnectAttempts]);

    useEffect(() => {
        connectSSE();

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [connectSSE]);

    // Polling fallback when SSE is disconnected
    useEffect(() => {
        let pollInterval;
        if (!isConnected) {
            pollInterval = setInterval(() => {
                refetchStats();
            }, 30000); // Poll every 30 seconds
        }
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [isConnected, refetchStats]);

    // Handle stats error
    if (statsError) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 text-center">
                    <svg className="w-12 h-12 text-red-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-lg font-bold text-red-700 dark:text-red-400">Unable to Load Dashboard</h3>
                    <p className="text-sm text-red-600 dark:text-red-300 mt-2">
                        {statsError.message || 'An error occurred while loading your dashboard data.'}
                    </p>
                    <button
                        onClick={() => refetchStats()}
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#3a663f] text-white rounded-xl hover:bg-[#2d5232] transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (statsLoading) return <SkeletonLoader />;

    const statsData = stats?.data || stats || {};
    const totalQuotes = statsData.totalQuotes || 0;
    const policies = statsData.policies || 0;
    const commissions = statsData.commissions || 0;
    const conversionRate = statsData.conversionRate || 0;
    const growthRate = statsData.growthRate || 0;
    const pendingQuotes = statsData.pendingQuotes || 0;

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8 animate-fadeIn">
            {/* Connection Status */}
            <div className="flex items-center justify-between bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-2xl px-6 py-3 border border-gray-100 dark:border-slate-700">
                <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`} />
                    <span className="text-sm text-gray-600 dark:text-slate-300">
                        {isConnected ? '🟢 Live Updates Connected' : '🟡 Polling Mode (Reconnecting...)'}
                    </span>
                    {lastUpdated && (
                        <span className="text-xs text-gray-400 dark:text-slate-500">
                            Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                        </span>
                    )}
                    {reconnectAttempts > 0 && (
                        <span className="text-xs text-amber-500">
                            Attempt {reconnectAttempts}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => refetchStats()}
                        className="p-2 text-gray-400 hover:text-[#3a663f] dark:hover:text-green-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                        title="Refresh data"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                    <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5">
                        {['day', 'week', 'month', 'year'].map((p) => (
                            <button
                                key={p}
                                onClick={() => setPeriod(p)}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${period === p
                                    ? 'bg-white dark:bg-slate-600 text-[#3a663f] dark:text-green-400 shadow-sm'
                                    : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white'
                                    }`}
                            >
                                {p.charAt(0).toUpperCase() + p.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Hero Banner */}
            <div className="relative bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54] rounded-3xl p-8 md:p-10 text-white overflow-hidden shadow-2xl group">
                <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3 group-hover:translate-x-1/4 transition-transform duration-1000" />
                <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/4 group-hover:-translate-x-1/3 transition-transform duration-1000" />

                <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="space-y-2">
                        <p className="text-green-200 text-sm uppercase tracking-widest">Agent Dashboard</p>
                        <h1 className="text-3xl md:text-4xl font-extrabold">
                            Hello, {user?.full_name || 'Agent'} 👋
                        </h1>
                        <p className="text-green-100/80 max-w-md">
                            Track your performance, manage quotes, and issue policies from one place.
                        </p>
                        <div className="flex gap-2 mt-3 flex-wrap">
                            <span className="px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-medium text-green-100 border border-white/10">
                                📋 {totalQuotes} Quotes
                            </span>
                            <span className="px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-medium text-green-100 border border-white/10">
                                📄 {policies} Policies
                            </span>
                            <span className="px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-medium text-green-100 border border-white/10">
                                💰 KES {(commissions || 0).toLocaleString()} Earned
                            </span>
                            {growthRate !== 0 && (
                                <span className={`px-3 py-1 backdrop-blur-sm rounded-full text-xs font-medium border border-white/10 ${growthRate > 0 ? 'bg-green-500/30 text-green-200' : 'bg-red-500/30 text-red-200'
                                    }`}>
                                    {growthRate > 0 ? '📈' : '📉'} {Math.abs(growthRate)}% Growth
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Link
                            to="/quotes/new"
                            className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 text-white font-semibold py-3 px-6 rounded-xl hover:bg-white hover:text-[#3a663f] transition-all duration-300 shadow-lg group/btn"
                        >
                            <svg className="w-5 h-5 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            New Quote
                        </Link>
                        <Link
                            to="/cargo"
                            className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 text-white font-semibold py-3 px-6 rounded-xl hover:bg-white hover:text-[#3a663f] transition-all duration-300 shadow-lg"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Cargo Certificate
                        </Link>
                    </div>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <StatCard
                    icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                    label="Total Quotes"
                    value={totalQuotes}
                    subValue={`${pendingQuotes} pending`}
                    gradient="from-[#2d5232] to-[#3a663f]"
                    accent="#3a663f"
                />
                <StatCard
                    icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    label="Active Policies"
                    value={policies}
                    subValue={`${Math.round((policies / (totalQuotes || 1)) * 100)}% conversion`}
                    gradient="from-[#3a663f] to-[#4d8c54]"
                    accent="#4d8c54"
                />
                <StatCard
                    icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    label="Commissions"
                    value={`KES ${(commissions || 0).toLocaleString()}`}
                    gradient="from-[#4d8c54] to-[#5fa06a]"
                    accent="#5fa06a"
                />
                <StatCard
                    icon={<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
                    label="Conversion Rate"
                    value={`${conversionRate || 0}%`}
                    subValue={`${growthRate > 0 ? '+' : ''}${growthRate}% vs last month`}
                    gradient="from-[#5fa06a] to-[#7ab885]"
                    accent="#7ab885"
                />
            </div>

            {/* Recent Activity Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                            <span className="w-1.5 h-6 bg-[#3a663f] rounded-full" />
                            Recent Quotes
                        </h3>
                        <Link to="/quotes" className="text-sm text-[#3a663f] dark:text-green-400 hover:underline font-medium flex items-center gap-1">
                            View All
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </Link>
                    </div>
                    {quotesLoading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-12 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />
                            ))}
                        </div>
                    ) : recentQuotes?.length > 0 ? (
                        <div className="space-y-3">
                            {recentQuotes.map((quote) => (
                                <QuoteItem key={quote.id} quote={quote} />
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No quotes yet. Create your first quote!" link="/quotes/new" linkText="Create Quote" />
                    )}
                </div>

                <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                            <span className="w-1.5 h-6 bg-[#3a663f] rounded-full" />
                            Recent Policies
                        </h3>
                        <Link to="/policies" className="text-sm text-[#3a663f] dark:text-green-400 hover:underline font-medium flex items-center gap-1">
                            View All
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </Link>
                    </div>
                    {policiesLoading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-12 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />
                            ))}
                        </div>
                    ) : recentPolicies?.length > 0 ? (
                        <div className="space-y-3">
                            {recentPolicies.map((policy) => (
                                <PolicyItem key={policy.id} policy={policy} />
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No policies issued yet. Convert a quote to get started!" link="/quotes/new" linkText="Create Quote" />
                    )}
                </div>
            </div>
        </div>
    );
}

// ========== STAT CARD ==========
function StatCard({ icon, label, value, subValue, gradient, accent }) {
    return (
        <div className={`relative group overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}>
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/5 blur-xl" />
            <div className="relative p-6 flex flex-col justify-between h-full">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-4 backdrop-blur-sm group-hover:scale-110 transition-transform">
                    {icon}
                </div>
                <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wider opacity-80">{label}</p>
                    <p className="text-3xl font-extrabold tracking-tight">{value}</p>
                    {subValue && <p className="text-xs opacity-70 mt-1">{subValue}</p>}
                </div>
                <div className="mt-4 h-1 w-12 rounded-full opacity-80 group-hover:w-16 transition-all" style={{ backgroundColor: accent }} />
            </div>
        </div>
    );
}

// ========== QUOTE ITEM ==========
function QuoteItem({ quote }) {
    return (
        <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group">
            <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 dark:text-white truncate">{quote.quote_number}</p>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                    <span className="truncate">{quote.vessel_name || 'N/A'}</span>
                    <span>•</span>
                    <span>KES {Number(quote.total_amount || 0).toLocaleString()}</span>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <Badge status={quote.status} />
                <Link to={`/quotes/${quote.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity text-sm text-[#3a663f] dark:text-green-400 hover:underline">
                    View
                </Link>
            </div>
        </div>
    );
}

// ========== POLICY ITEM ==========
function PolicyItem({ policy }) {
    return (
        <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group">
            <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 dark:text-white truncate">{policy.policy_number}</p>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                    <span className="truncate">{policy.vessel_name || 'N/A'}</span>
                    <span>•</span>
                    <span>KES {Number(policy.sum_insured || 0).toLocaleString()}</span>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <Badge status={policy.payment_status || 'active'} />
                <Link to={`/policies/${policy.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity text-sm text-[#3a663f] dark:text-green-400 hover:underline">
                    View
                </Link>
            </div>
        </div>
    );
}

// ========== EMPTY STATE ==========
function EmptyState({ message, link, linkText }) {
    return (
        <div className="text-center py-8">
            <svg className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-gray-400 dark:text-slate-500 text-sm mb-3">{message}</p>
            {link && (
                <Link to={link} className="inline-flex items-center gap-1.5 text-sm text-[#3a663f] dark:text-green-400 font-medium hover:text-[#2d5232] dark:hover:text-green-300 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {linkText}
                </Link>
            )}
        </div>
    );
}

// ========== SKELETON LOADER ==========
function SkeletonLoader() {
    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8 animate-pulse">
            <div className="h-48 bg-gray-200 dark:bg-slate-700 rounded-3xl animate-pulse" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-36 bg-gray-100 dark:bg-slate-700 rounded-2xl animate-pulse" />
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                    <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-1/3 animate-pulse" />
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-14 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />
                    ))}
                </div>
                <div className="space-y-4">
                    <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-1/3 animate-pulse" />
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-14 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />
                    ))}
                </div>
            </div>
        </div>
    );
}